import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { 
  Car, Droplets, CircleDot, Shield, MapPin, 
  CalendarIcon, Clock, ChevronRight, Check, CreditCard,
  Home, Building2, Star, Crown, Award, Wrench
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Services mobilité avec prix déplacement
const SERVICES_MOBILITE = {
  lavage: [
    { id: 'clean-mobile', name: 'CLEAN', price: 35, icon: Droplets, color: 'text-blue-400', description: 'Prélavage, Lavage carrosserie, Jantes, Brillant pneus' },
    { id: 'vip-mobile', name: 'VIP', price: 65, icon: Star, color: 'text-yellow-400', description: 'CLEAN + Essuyage intérieur porte, Vitres, Essuyage plastiques, Aspiration, Parfum', popular: true },
    { id: 'platine-mobile', name: 'PLATINE', price: 85, icon: Crown, color: 'text-purple-400', description: 'VIP + Shampoing tapis' },
    { id: 'prestige-mobile', name: 'PRESTIGE', price: 125, icon: Award, color: 'text-red-500', description: 'PLATINE + Traitement cuir, Cire protection' },
  ],
  pneumatique: [
    { id: 'crevaison-mobile', name: 'Réparation Crevaison', price: 45, icon: CircleDot, color: 'text-zinc-400', description: 'Réparation de pneu crevé sur place' },
    { id: 'changement-1-mobile', name: 'Changement 1 Pneu', price: 35, icon: Wrench, color: 'text-zinc-400', description: 'Montage + équilibrage (pneu non fourni)' },
    { id: 'changement-2-mobile', name: 'Changement 2 Pneus', price: 60, icon: Wrench, color: 'text-zinc-400', description: 'Montage + équilibrage (pneus non fournis)' },
    { id: 'changement-4-mobile', name: 'Changement 4 Pneus', price: 100, icon: Wrench, color: 'text-zinc-400', description: 'Montage + équilibrage (pneus non fournis)' },
  ],
  vitrage: [
    { id: 'impact-mobile', name: 'Réparation Impact', price: 60, icon: Shield, color: 'text-cyan-400', description: 'Réparation impact pare-brise' },
    { id: 'parebrise-mobile', name: 'Remplacement Pare-brise', price: 'Sur devis', icon: Shield, color: 'text-cyan-400', description: 'Devis selon véhicule', isDevis: true },
    { id: 'vitre-mobile', name: 'Vitre Latérale', price: 'Sur devis', icon: Shield, color: 'text-cyan-400', description: 'Devis selon véhicule', isDevis: true },
  ]
};

const TIME_SLOTS = [
  '08:00', '09:00', '10:00', '11:00', '12:00', 
  '14:00', '15:00', '16:00', '17:00', '18:00'
];

const ZONES = {
  petiteCouronne: {
    name: 'Paris & Petite Couronne',
    departments: ['75', '92', '93', '94'],
    supplement: 10,
    description: 'Paris, Hauts-de-Seine, Seine-Saint-Denis, Val-de-Marne'
  },
  grandeCouronne: {
    name: 'Grande Couronne',
    departments: ['77', '78', '91', '95'],
    supplement: 20,
    description: 'Seine-et-Marne, Yvelines, Essonne, Val-d\'Oise'
  }
};

const getZoneFromPostalCode = (postalCode) => {
  if (!postalCode || postalCode.length < 2) return null;
  const dept = postalCode.substring(0, 2);
  if (ZONES.petiteCouronne.departments.includes(dept)) return 'petiteCouronne';
  if (ZONES.grandeCouronne.departments.includes(dept)) return 'grandeCouronne';
  return null;
};

const CATEGORIES = [
  { id: 'lavage', name: 'Lavage', icon: Droplets, color: 'text-blue-400' },
  { id: 'pneumatique', name: 'Pneumatique', icon: CircleDot, color: 'text-zinc-400' },
  { id: 'vitrage', name: 'Vitrage', icon: Shield, color: 'text-cyan-400' },
];

export default function ReservationMobilitePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState('lavage');
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [locationType, setLocationType] = useState('domicile');
  const [address, setAddress] = useState({
    street: '',
    complement: '',
    postalCode: '',
    city: '',
    companyName: '',
    contactPerson: '',
  });
  const [zone, setZone] = useState(null);
  const [vehicleInfo, setVehicleInfo] = useState({
    type: 'auto',
    brand: '',
    model: '',
    plate: '',
  });
  const [loading, setLoading] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const services = SERVICES_MOBILITE[category] || [];
  
  // Calculate total price based on zone
  const getServicePrice = (service) => {
    if (service.isDevis) return 'Sur devis';
    const basePriceCenter = service.price - 10; // Prix centre (sans déplacement)
    const supplement = zone ? ZONES[zone].supplement : 10;
    return basePriceCenter + supplement;
  };
  
  const totalPrice = selectedService && !selectedService.isDevis && zone
    ? getServicePrice(selectedService)
    : selectedService?.price || 0;

  const handleNext = () => {
    if (step === 1 && !selectedService) {
      toast.error('Veuillez sélectionner un service');
      return;
    }
    if (step === 2 && (!address.street || !address.postalCode || !address.city)) {
      toast.error('Veuillez remplir l\'adresse complète');
      return;
    }
    if (step === 2 && locationType === 'bureau' && (!address.companyName || !address.contactPerson)) {
      toast.error('Veuillez renseigner le nom de la société et l\'interlocuteur');
      return;
    }
    if (step === 2) {
      const detectedZone = getZoneFromPostalCode(address.postalCode);
      if (!detectedZone) {
        toast.error('Nous n\'intervenons pas dans cette zone. Veuillez entrer un code postal d\'Île-de-France.');
        return;
      }
      setZone(detectedZone);
    }
    if (step === 3 && (!selectedDate || !selectedTime)) {
      toast.error('Veuillez sélectionner une date et un horaire');
      return;
    }
    if (step === 4 && (!vehicleInfo.brand || !vehicleInfo.plate)) {
      toast.error('Veuillez renseigner les informations du véhicule');
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handlePayment = async () => {
    if (!user) {
      toast.error('Veuillez vous connecter pour réserver');
      navigate('/connexion');
      return;
    }

    if (selectedService.isDevis) {
      toast.info('Ce service nécessite un devis personnalisé');
      navigate('/devis');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create mobile booking
      const bookingData = {
        service_id: selectedService.id,
        service_name: selectedService.name,
        service_type: 'mobilite',
        category: category,
        price: totalPrice,
        zone: zone,
        zone_supplement: ZONES[zone].supplement,
        date: format(selectedDate, 'yyyy-MM-dd'),
        time: selectedTime,
        location_type: locationType,
        address: `${address.street}${address.complement ? ', ' + address.complement : ''}, ${address.postalCode} ${address.city}`,
        vehicle_type: vehicleInfo.type,
        vehicle_brand: vehicleInfo.brand,
        vehicle_model: vehicleInfo.model,
        vehicle_plate: vehicleInfo.plate,
      };

      const bookingResponse = await fetch(`${API_URL}/api/mobile-bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bookingData)
      });

      if (!bookingResponse.ok) {
        throw new Error('Erreur lors de la réservation');
      }

      const booking = await bookingResponse.json();

      // Create checkout session
      const checkoutResponse = await fetch(`${API_URL}/api/mobile-bookings/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          booking_id: booking.id,
          origin_url: window.location.origin
        })
      });

      if (!checkoutResponse.ok) {
        throw new Error('Erreur lors du paiement');
      }

      const checkout = await checkoutResponse.json();
      window.location.href = checkout.url;

    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Erreur lors de la réservation. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Hero */}
      <section className="bg-gradient-to-b from-zinc-900 to-[#09090B] py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-orange-600/20 text-orange-400 px-4 py-2 rounded-full border border-orange-600/30 mb-4">
            <Car size={18} />
            <span className="text-sm font-medium">Réservation mobilité</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">
            RÉSERVER UNE <span className="text-red-500">INTERVENTION</span>
          </h1>
          <p className="text-zinc-400">À domicile ou au bureau • Paiement sécurisé en ligne</p>
        </div>
      </section>

      {/* Progress */}
      <section className="py-6 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {['Service', 'Adresse', 'Date & Heure', 'Véhicule', 'Paiement'].map((label, idx) => (
              <div key={idx} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step > idx + 1 ? 'bg-green-600 text-white' :
                  step === idx + 1 ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-500'
                }`}>
                  {step > idx + 1 ? <Check size={20} /> : idx + 1}
                </div>
                <span className={`ml-2 hidden sm:block ${step === idx + 1 ? 'text-white' : 'text-zinc-500'}`}>
                  {label}
                </span>
                {idx < 4 && <ChevronRight className="mx-2 text-zinc-700" size={20} />}
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Step 1: Service Selection */}
        {step === 1 && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Choisissez votre service</h2>
            
            {/* Category Tabs */}
            <div className="flex gap-2 mb-8">
              {CATEGORIES.map((cat) => (
                <Button
                  key={cat.id}
                  variant={category === cat.id ? 'default' : 'ghost'}
                  onClick={() => { setCategory(cat.id); setSelectedService(null); }}
                  className={category === cat.id ? 'bg-red-600' : 'text-zinc-400'}
                >
                  <cat.icon size={18} className="mr-2" />
                  {cat.name}
                </Button>
              ))}
            </div>

            {/* Services Grid */}
            <div className="grid md:grid-cols-2 gap-4">
              {services.map((service) => (
                <Card
                  key={service.id}
                  className={`bg-zinc-900/50 border-zinc-800 cursor-pointer transition-all ${
                    selectedService?.id === service.id ? 'ring-2 ring-red-500 border-red-500/50' : 'hover:border-zinc-700'
                  } ${service.popular ? 'border-yellow-500/30' : ''}`}
                  onClick={() => setSelectedService(service)}
                  data-testid={`service-${service.id}`}
                >
                  {service.popular && (
                    <div className="bg-yellow-500 text-black text-xs text-center py-1 font-bold">POPULAIRE</div>
                  )}
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl bg-zinc-800 flex items-center justify-center flex-shrink-0`}>
                        <service.icon className={service.color} size={24} />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <h3 className="text-lg font-bold text-white">{service.name}</h3>
                          <span className="text-xl font-bold text-red-500">
                            {service.isDevis ? 'Devis' : `${service.price}€`}
                          </span>
                        </div>
                        <p className="text-zinc-400 text-sm mt-1">{service.description}</p>
                        {!service.isDevis && (
                          <p className="text-orange-400 text-xs mt-2">Déplacement inclus</p>
                        )}
                      </div>
                      {selectedService?.id === service.id && (
                        <Check className="text-green-500" size={24} />
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Address */}
        {step === 2 && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Lieu d'intervention</h2>
            
            {/* Location Type */}
            <div className="flex gap-4 mb-8">
              <Card
                className={`flex-1 cursor-pointer transition-all ${
                  locationType === 'domicile' ? 'ring-2 ring-red-500 border-red-500/50' : 'bg-zinc-900/50 border-zinc-800'
                }`}
                onClick={() => setLocationType('domicile')}
              >
                <CardContent className="p-6 text-center">
                  <Home className={`mx-auto mb-2 ${locationType === 'domicile' ? 'text-red-500' : 'text-zinc-400'}`} size={32} />
                  <p className="text-white font-semibold">À domicile</p>
                </CardContent>
              </Card>
              <Card
                className={`flex-1 cursor-pointer transition-all ${
                  locationType === 'bureau' ? 'ring-2 ring-red-500 border-red-500/50' : 'bg-zinc-900/50 border-zinc-800'
                }`}
                onClick={() => setLocationType('bureau')}
              >
                <CardContent className="p-6 text-center">
                  <Building2 className={`mx-auto mb-2 ${locationType === 'bureau' ? 'text-red-500' : 'text-zinc-400'}`} size={32} />
                  <p className="text-white font-semibold">Au bureau</p>
                </CardContent>
              </Card>
            </div>

            {/* Address Form */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-6 space-y-4">
                {/* Company Info - Only for Bureau */}
                {locationType === 'bureau' && (
                  <>
                    <div>
                      <Label className="text-zinc-400">Nom de la société *</Label>
                      <Input
                        placeholder="Nom de l'entreprise"
                        value={address.companyName}
                        onChange={(e) => setAddress({...address, companyName: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400">Interlocuteur *</Label>
                      <Input
                        placeholder="Nom et prénom de la personne à contacter"
                        value={address.contactPerson}
                        onChange={(e) => setAddress({...address, contactPerson: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      />
                    </div>
                  </>
                )}
                <div>
                  <Label className="text-zinc-400">Adresse *</Label>
                  <Input
                    placeholder="Numéro et nom de rue"
                    value={address.street}
                    onChange={(e) => setAddress({...address, street: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Complément d'adresse</Label>
                  <Input
                    placeholder="Bâtiment, étage, code d'accès..."
                    value={address.complement}
                    onChange={(e) => setAddress({...address, complement: e.target.value})}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400">Code postal *</Label>
                    <Input
                      placeholder="75016"
                      value={address.postalCode}
                      onChange={(e) => {
                        const newPostalCode = e.target.value;
                        setAddress({...address, postalCode: newPostalCode});
                        const detectedZone = getZoneFromPostalCode(newPostalCode);
                        if (detectedZone) setZone(detectedZone);
                      }}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Ville *</Label>
                    <Input
                      placeholder="Paris"
                      value={address.city}
                      onChange={(e) => setAddress({...address, city: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                </div>
                
                {/* Zone Info */}
                {address.postalCode.length >= 2 && (
                  <div className={`p-4 rounded-lg border ${
                    zone === 'petiteCouronne' 
                      ? 'bg-green-500/10 border-green-500/30' 
                      : zone === 'grandeCouronne'
                        ? 'bg-orange-500/10 border-orange-500/30'
                        : 'bg-red-500/10 border-red-500/30'
                  }`}>
                    {zone ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-semibold ${zone === 'petiteCouronne' ? 'text-green-400' : 'text-orange-400'}`}>
                            {ZONES[zone].name}
                          </p>
                          <p className="text-zinc-400 text-sm">{ZONES[zone].description}</p>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-bold ${zone === 'petiteCouronne' ? 'text-green-400' : 'text-orange-400'}`}>
                            +{ZONES[zone].supplement}€
                          </p>
                          <p className="text-zinc-500 text-xs">déplacement</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-red-400 text-sm">
                        Zone non couverte. Nous intervenons uniquement en Île-de-France.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Date & Time */}
        {step === 3 && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Date et horaire</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-6">
                  <Label className="text-zinc-400 mb-4 block">Sélectionnez une date</Label>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => date < new Date() || date.getDay() === 0}
                    className="rounded-md border border-zinc-800"
                  />
                </CardContent>
              </Card>
              
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-6">
                  <Label className="text-zinc-400 mb-4 block">Sélectionnez un horaire</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {TIME_SLOTS.map((time) => (
                      <Button
                        key={time}
                        variant={selectedTime === time ? 'default' : 'outline'}
                        onClick={() => setSelectedTime(time)}
                        className={selectedTime === time 
                          ? 'bg-red-600 hover:bg-red-700' 
                          : 'border-zinc-700 text-white hover:bg-zinc-800'
                        }
                      >
                        <Clock size={16} className="mr-2" />
                        {time}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 4: Vehicle Info */}
        {step === 4 && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Informations véhicule</h2>
            
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label className="text-zinc-400 mb-2 block">Type de véhicule</Label>
                  <div className="flex gap-4">
                    {['auto', 'moto', 'scooter'].map((type) => (
                      <Button
                        key={type}
                        variant={vehicleInfo.type === type ? 'default' : 'outline'}
                        onClick={() => setVehicleInfo({...vehicleInfo, type})}
                        className={vehicleInfo.type === type 
                          ? 'bg-red-600' 
                          : 'border-zinc-700 text-white'
                        }
                      >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400">Marque *</Label>
                    <Input
                      placeholder="ex: Peugeot"
                      value={vehicleInfo.brand}
                      onChange={(e) => setVehicleInfo({...vehicleInfo, brand: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400">Modèle</Label>
                    <Input
                      placeholder="ex: 308"
                      value={vehicleInfo.model}
                      onChange={(e) => setVehicleInfo({...vehicleInfo, model: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-400">Immatriculation *</Label>
                  <Input
                    placeholder="ex: AB-123-CD"
                    value={vehicleInfo.plate}
                    onChange={(e) => setVehicleInfo({...vehicleInfo, plate: e.target.value.toUpperCase()})}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 5: Payment Summary */}
        {step === 5 && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6">Récapitulatif & Paiement</h2>
            
            <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
              <CardContent className="p-6 space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-zinc-800">
                  <div>
                    <p className="text-zinc-400 text-sm">Service</p>
                    <p className="text-white font-bold text-lg">{selectedService?.name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-red-500">{totalPrice}€</p>
                    {zone && (
                      <p className="text-zinc-500 text-xs">dont +{ZONES[zone].supplement}€ déplacement</p>
                    )}
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-zinc-400 text-sm">Lieu d'intervention</p>
                    <p className="text-white flex items-center gap-2">
                      {locationType === 'domicile' ? <Home size={16} /> : <Building2 size={16} />}
                      {locationType === 'domicile' ? 'Domicile' : 'Bureau'}
                    </p>
                    <p className="text-zinc-400 text-sm mt-1">{address.street}</p>
                    <p className="text-zinc-400 text-sm">{address.postalCode} {address.city}</p>
                    {zone && (
                      <p className={`text-xs mt-1 ${zone === 'petiteCouronne' ? 'text-green-400' : 'text-orange-400'}`}>
                        {ZONES[zone].name}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Date & Heure</p>
                    <p className="text-white flex items-center gap-2">
                      <CalendarIcon size={16} />
                      {selectedDate && format(selectedDate, 'dd MMMM yyyy', { locale: fr })}
                    </p>
                    <p className="text-white flex items-center gap-2 mt-1">
                      <Clock size={16} />
                      {selectedTime}
                    </p>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-zinc-800">
                  <p className="text-zinc-400 text-sm">Véhicule</p>
                  <p className="text-white">
                    {vehicleInfo.brand} {vehicleInfo.model} • {vehicleInfo.plate}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-green-900/20 border-green-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-400 font-bold text-lg">Total à payer</p>
                    <p className="text-zinc-400 text-sm">Paiement sécurisé par Stripe</p>
                  </div>
                  <p className="text-4xl font-bold text-white">{totalPrice}€</p>
                </div>
              </CardContent>
            </Card>

            {!user && (
              <div className="mt-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 text-center">
                <p className="text-yellow-400">Vous devez être connecté pour finaliser la réservation</p>
                <Button onClick={() => navigate('/connexion')} className="mt-2 bg-yellow-500 text-black hover:bg-yellow-600">
                  Se connecter
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          {step > 1 ? (
            <Button variant="outline" onClick={handleBack} className="border-zinc-700 text-white">
              Retour
            </Button>
          ) : (
            <div></div>
          )}
          
          {step < 5 ? (
            <Button onClick={handleNext} className="bg-red-600 hover:bg-red-700">
              Continuer
              <ChevronRight size={18} className="ml-1" />
            </Button>
          ) : (
            <Button 
              onClick={handlePayment} 
              disabled={loading || !user || selectedService?.isDevis}
              className="bg-green-600 hover:bg-green-700 text-lg px-8"
            >
              {loading ? 'Chargement...' : (
                <>
                  <CreditCard size={20} className="mr-2" />
                  Payer {totalPrice}€
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
